/* ------------------------------------------------------------------------------------------------------------------
   EVO promo video – the video tiles inside the collection grid (snippets/evo-collection-promo).

   A plain `autoplay` video starts running while the page loads, no matter where the tile sits. The tiles are far
   below the fold, so by the time someone scrolls down to a tile the clip has played for a while and is shown
   mid-way (a problem for clips with an intro, e.g. the founder video on "Alle Produkte").

   This element keeps the video paused until the tile is in view and always starts it at 0:00:
   - first time in view: play from the start (the poster is frame 0, so there is no jump)
   - scrolled out of view: pause (saves battery / data)
   - back in view after it was completely out of view: rewind to 0:00 and play again
   - restored from the back/forward cache: rewind, so it never resumes in the middle
   ------------------------------------------------------------------------------------------------------------------ */

(() => {
  if (window.customElements.get('evo-promo-video')) return;

  // share of the tile that has to be visible before the video runs
  const PLAY_THRESHOLD = 0.3;

  class EvoPromoVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;

      this.inView = false;
      this.rewindOnNextPlay = false;

      this.video.pause();

      this.observer = new IntersectionObserver((entries) => this.onIntersect(entries[entries.length - 1]), {
        threshold: [0, PLAY_THRESHOLD],
      });
      // the wrapper is display: contents (no box), so the video itself is observed
      this.observer.observe(this.video);

      this.onPageShow = (event) => {
        if (!event.persisted) return;
        this.video.currentTime = 0;
        this.rewindOnNextPlay = false;
        if (this.inView) this.play();
      };
      window.addEventListener('pageshow', this.onPageShow);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.onPageShow) window.removeEventListener('pageshow', this.onPageShow);
    }

    onIntersect(entry) {
      if (entry.isIntersecting && entry.intersectionRatio >= PLAY_THRESHOLD) {
        this.inView = true;

        if (this.rewindOnNextPlay) {
          this.video.currentTime = 0;
          this.rewindOnNextPlay = false;
        }

        this.play();
        return;
      }

      this.inView = false;
      this.video.pause();

      // completely out of view: the next time the tile shows up the video starts over
      if (!entry.isIntersecting) this.rewindOnNextPlay = true;
    }

    play() {
      const promise = this.video.play();
      if (promise && typeof promise.catch === 'function') promise.catch(() => {});
    }
  }

  window.customElements.define('evo-promo-video', EvoPromoVideo);
})();
